import { ReactNode, useMemo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { SxProps, useTheme } from "@mui/material/styles";

type WidgetButtonProps = {
  sx?: SxProps;
  onClick?: (event: any) => void;
  tooltip?: string;
  children?: ReactNode;
  startIcon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
};

const containerSx = {
  alignItems: "center",
  padding: 0,
  margin: 0
};

export default function WidgetButton({
  children,
  startIcon,
  sx,
  onClick,
  tooltip,
  disabled,
  loading
}: WidgetButtonProps) {
  const theme = useTheme();

  const sxValue: SxProps = useMemo(
    () => ({
      boxShadow: theme.shadows[3],
      borderRadius: "16px",
      background: "hsla(272, 17%, 34%, 0.85)",
      boxSizing: "border-box",
      width: "fit-content",
      height: "34px",
      cursor: "pointer",
      minWidth: "auto", // Reset Button's default minWidth
      textTransform: "none", // Reset Button's default textTransform
      color: "white", // Set explicit white color

      "&.MuiButton-root": {
        px: 1.5
      },
      "&:hover": {
        background: "hsla(271, 18%, 27%, 0.85)",
        color: "white"
      },
      ...sx
    }),
    [theme.shadows, sx]
  );

  return (
    <Tooltip title={tooltip} disableInteractive>
      <Button
        data-disable-drag="true"
        sx={sxValue}
        onClick={loading ? undefined : onClick}
        onTouchEnd={loading ? undefined : onClick}
        disableRipple
        disableElevation
        disabled={disabled}
      >
        <Stack width="100%" height="100%" direction="row" sx={containerSx}>
          {loading ? (
            <Box sx={{ pr: 0.5 }}>
              <CircularProgress size={16} sx={{ color: "white" }} />
            </Box>
          ) : (
            startIcon
          )}
          {children}
        </Stack>
      </Button>
    </Tooltip>
  );
}
